<template>
  <section
    v-if="visible && safeTasks.length > 0"
    class="paper-download-list"
    :class="{ collapsed, dragging: isDragging }"
    :style="panelStyle"
  >
    <header @mousedown="startDrag" @touchstart="startDrag">
      <strong>{{ displayTitle }}</strong>
      <span>{{ displaySummary }}</span>
      <button type="button" @click="$emit('toggle-collapse')">{{ collapsed ? ui.expand : ui.collapse }}</button>
      <button type="button" @click="$emit('close')">{{ ui.close }}</button>
    </header>
    <div v-if="!collapsed" class="paper-download-task-list">
      <div v-for="task in safeTasks" :key="task.id" class="paper-download-task">
        <div class="paper-download-task-head">
          <strong :title="task.fileName">{{ task.fileName }}</strong>
          <span>{{ taskProgressText(task) }}%</span>
        </div>
        <div class="paper-download-task-foot">
          <span class="paper-download-task-status">
            <span class="paper-download-task-status-text">{{ taskStatusPrefix(task) }}</span>
            <button
              v-if="hasBundleItemList(task)"
              type="button"
              class="paper-download-task-progress-button"
              @click.stop="toggleBundlePopover(task, $event)"
            >
              <span class="paper-download-task-progress-bracket">（</span>
              <span class="paper-download-task-progress-number">{{ taskBundleProgressValue(task) }}</span>
              <span class="paper-download-task-progress-bracket">）</span>
            </button>
          </span>
          <button v-if="task.state == 'done' && canOpenTask(task)" type="button" @click="$emit('open-task', task)">{{ ui.open }}</button>
          <button v-if="task.state == 'error'" type="button" @click="$emit('retry-task', task)">{{ ui.retry }}</button>
          <button type="button" @click="$emit('remove-task', task)">{{ task.state == 'running' || task.state == 'pending' ? ui.cancel : ui.clear }}</button>
        </div>
        <div class="paper-download-progress">
          <i :style="{ width: task.progress + '%' }"></i>
        </div>
      </div>
    </div>
    <div
      v-if="bundlePopover.visible && bundlePopoverTask"
      ref="bundlePopover"
      class="paper-download-bundle-popover"
      :class="bundlePopoverPlacementClass"
      :style="bundlePopoverStyle"
      @mousedown.stop
      @touchstart.stop
    >
      <div class="paper-download-bundle-popover-arrow" :style="bundlePopoverArrowStyle"></div>
      <header>
        <div class="paper-download-bundle-popover-title">
          <strong :title="bundlePopoverTask.fileName">{{ bundlePopoverTask.fileName }}</strong>
          <span>（{{ bundlePopoverSummary }}）</span>
        </div>
        <button type="button" @click="closeBundlePopover">{{ ui.close }}</button>
      </header>
      <div class="paper-download-bundle-popover-list">
        <div v-for="item in bundlePopoverItems" :key="item.id" class="paper-download-bundle-popover-item">
          <div class="paper-download-bundle-popover-item-head">
            <strong :title="item.name">{{ item.name }}</strong>
            <span>{{ item.progress }}%</span>
          </div>
          <div class="paper-download-bundle-popover-item-foot">
            <span class="paper-download-bundle-popover-item-status">{{ item.statusText }}</span>
            <button v-if="item.state == 'error'" type="button" @click="$emit('retry-bundle-item', bundlePopoverTask, item)">{{ ui.retry }}</button>
          </div>
          <div class="paper-download-progress">
            <i :style="{ width: item.progress + '%' }"></i>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { getAppUiText } from '@/utils/language/common'

export default {
  name: 'PaperDownloadList',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    tasks: {
      type: Array,
      default() {
        return []
      }
    },
    summary: {
      type: String,
      default: ''
    },
    language: {
      type: String,
      default: 'us'
    },
    title: {
      type: String,
      default: ''
    },
    canOpenTask: {
      type: Function,
      default() {
        return false
      }
    }
  },
  data() {
    return {
      dragPosition: null,
      dragState: null,
      previousBodyUserSelect: '',
      bundlePopover: {
        visible: false,
        taskId: '',
        left: 0,
        top: 0,
        width: 420,
        maxHeight: 320,
        arrowLeft: 32,
        anchorLeft: 0,
        anchorTop: 0,
        anchorWidth: 0,
        anchorHeight: 0,
        placement: 'below'
      },
      bundleFailureDialog: {
        visible: false,
        taskId: '',
        itemId: ''
      }
    }
  },
  created() {
    this.panelResizeObserver = null
    this.panelResizeObserverTarget = null
    this.panelHeightReportPending = false
    this.reportedPanelHeight = -1
  },
  computed: {
    ui() {
      return getAppUiText(this.language)
    },
    displayTitle() {
      return this.title || this.ui.downloadList
    },
    displaySummary() {
      const summary = String(this.summary || '')
      const count = summary.match(/\d+\s*\/\s*\d+/)
      return count ? count[0].replace(/\s+/g, '') : ''
    },
    safeTasks() {
      return Array.isArray(this.tasks) ? this.tasks : []
    },
    isDragging() {
      return !!this.dragState
    },
    bundlePopoverTask() {
      return this.safeTasks.find(task => task && task.id == this.bundlePopover.taskId) || null
    },
    bundleFailureDialogTask() {
      return this.safeTasks.find(task => task && task.id == this.bundleFailureDialog.taskId) || null
    },
    bundleFailureDialogItem() {
      const task = this.bundleFailureDialogTask
      if (!task || !Array.isArray(task.bundleItems)) {
        return null
      }
      return task.bundleItems.find(item => item && item.id == this.bundleFailureDialog.itemId) || null
    },
    bundleFailureDialogItemName() {
      const task = this.bundleFailureDialogTask
      const item = this.bundleFailureDialogItem
      return String(item && item.name || item && item.fileName || task && task.fileName || '').trim() || this.ui.downloadFailed || '下载失败'
    },
    bundleFailureDialogReason() {
      const item = this.bundleFailureDialogItem
      const text = String(item && (item.failureReason || item.errorMessage || item.statusText) || '').trim()
      return text || this.ui.downloadFailed || '下载失败'
    },
    bundlePopoverItems() {
      const task = this.bundlePopoverTask
      return Array.isArray(task && task.bundleItems) ? task.bundleItems : []
    },
    bundlePopoverSummary() {
      const task = this.bundlePopoverTask
      if (!task) {
        return ''
      }
      return this.taskBundleProgressValue(task)
    },
    bundlePopoverPlacementClass() {
      return this.bundlePopover.placement == 'above' ? 'is-above' : 'is-below'
    },
    bundlePopoverStyle() {
      return {
        left: `${this.bundlePopover.left}px`,
        top: `${this.bundlePopover.top}px`,
        width: `${this.bundlePopover.width}px`,
        maxHeight: `${this.bundlePopover.maxHeight}px`
      }
    },
    bundlePopoverArrowStyle() {
      return {
        left: `${this.bundlePopover.arrowLeft}px`
      }
    },
    panelStyle() {
      if (!this.dragPosition) {
        return null
      }
      return {
        left: `${this.dragPosition.left}px`,
        top: `${this.dragPosition.top}px`,
        right: 'auto',
        bottom: 'auto'
      }
    }
  },
  watch: {
    collapsed(value, oldValue) {
      this.keepPanelBottomAfterSizeChange(value, oldValue)
      this.schedulePanelHeightReport()
    },
    visible(value) {
      if (!value) {
        this.closeBundlePopover()
        this.closeBundleFailureDialog()
        this.stopDrag()
        this.schedulePanelHeightReport()
        return
      }
      this.$nextTick(this.keepPanelInViewport)
      this.schedulePanelHeightReport()
    },
    safeTasks(value) {
      if (!value || value.length == 0) {
        this.closeBundlePopover()
        this.closeBundleFailureDialog()
        this.stopDrag()
        this.schedulePanelHeightReport()
        return
      }
      if (this.bundlePopover.visible && !this.bundlePopoverTask) {
        this.closeBundlePopover()
      }
      if (this.bundleFailureDialog.visible && !this.bundleFailureDialogItem) {
        this.closeBundleFailureDialog()
      }
      this.$nextTick(this.keepPanelInViewport)
      this.schedulePanelHeightReport()
    }
  },
  updated() {
    this.schedulePanelHeightReport()
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.keepPanelInViewport)
      window.addEventListener('resize', this.handlePanelResize)
    }
    this.schedulePanelHeightReport()
  },
  beforeDestroy() {
    this.removeDragListeners()
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.keepPanelInViewport)
      window.removeEventListener('resize', this.handlePanelResize)
    }
    this.disconnectPanelResizeObserver()
  },
  methods: {
    getPanelElement() {
      const panel = this.$el
      if (!panel || panel.nodeType !== 1 || typeof panel.getBoundingClientRect != 'function') {
        return null
      }
      return panel
    },
    schedulePanelHeightReport() {
      if (this._isBeingDestroyed || this._isDestroyed || this.panelHeightReportPending) {
        return
      }
      this.panelHeightReportPending = true
      this.$nextTick(() => {
        this.panelHeightReportPending = false
        if (this._isBeingDestroyed || this._isDestroyed) {
          return
        }
        this.observePanelResize()
        this.reportPanelHeight()
      })
    },
    observePanelResize() {
      const panel = this.getPanelElement()
      if (typeof window === 'undefined' || typeof window.ResizeObserver != 'function') {
        return
      }
      if (!panel) {
        this.disconnectPanelResizeObserver()
        return
      }
      if (this.panelResizeObserverTarget === panel && this.panelResizeObserver) {
        return
      }
      this.disconnectPanelResizeObserver()
      try {
        this.panelResizeObserver = new window.ResizeObserver(() => {
          this.schedulePanelHeightReport()
        })
        this.panelResizeObserverTarget = panel
        this.panelResizeObserver.observe(panel)
      } catch (error) {
        this.panelResizeObserver = null
        this.panelResizeObserverTarget = null
      }
    },
    disconnectPanelResizeObserver() {
      if (this.panelResizeObserver && typeof this.panelResizeObserver.disconnect == 'function') {
        this.panelResizeObserver.disconnect()
      }
      this.panelResizeObserver = null
      this.panelResizeObserverTarget = null
    },
    reportPanelHeight() {
      const panel = this.getPanelElement()
      const rect = panel ? panel.getBoundingClientRect() : null
      const rawHeight = this.visible && this.safeTasks.length > 0 && rect ? Number(rect.height) : 0
      const height = Number.isFinite(rawHeight) && rawHeight > 0
        ? Math.round(rawHeight * 100) / 100
        : 0
      if (this.reportedPanelHeight == height) {
        return
      }
      this.reportedPanelHeight = height
      this.$emit('panel-height-change', height)
    },
    handlePanelResize() {
      this.schedulePanelHeightReport()
    },
    hasBundleItemList(task) {
      return !!(task && Array.isArray(task.bundleItems) && task.bundleItems.length > 0)
    },
    taskProgressText(task) {
      const value = Number(task && task.progress)
      return Number.isFinite(value) ? Math.max(0, Math.min(100, Math.round(value))) : 0
    },
    taskStatusPrefix(task) {
      const text = String(task && task.statusText || '').trim()
      if (!this.hasBundleItemList(task)) {
        return text
      }
      return text.replace(/\s*（\d+\/\d+）\s*$/, '') || text
    },
    taskBundleProgressText(task) {
      if (!this.hasBundleItemList(task)) {
        return ''
      }
      const current = Number(task && task.bundleProgressCurrent)
      const total = Number(task && task.bundleProgressTotal)
      if (!Number.isFinite(current) || !Number.isFinite(total) || total <= 0) {
        const text = String(task && task.statusText || '')
        const match = text.match(/（\d+\/\d+）$/)
        return match ? match[0] : ''
      }
      return `（${Math.max(0, Math.min(Math.round(current), total))}/${Math.max(0, Math.round(total))}）`
    },
    taskBundleProgressValue(task) {
      if (!this.hasBundleItemList(task)) {
        return ''
      }
      const current = Number(task && task.bundleProgressCurrent)
      const total = Number(task && task.bundleProgressTotal)
      if (Number.isFinite(current) && Number.isFinite(total) && total > 0) {
        return `${Math.max(0, Math.min(Math.round(current), total))}/${Math.max(0, Math.round(total))}`
      }
      const text = String(task && task.statusText || '')
      const match = text.match(/(\d+)\s*\/\s*(\d+)/)
      return match ? `${match[1]}/${match[2]}` : '0/0'
    },
    getBundlePopoverAnchorRect(event) {
      const target = event && event.currentTarget
      if (!target || typeof target.getBoundingClientRect != 'function') {
        return null
      }
      return target.getBoundingClientRect()
    },
    clampBundlePopoverPosition(value, min, max) {
      return Math.min(Math.max(value, min), max)
    },
    calculateBundlePopoverPosition(task, anchorRect) {
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0
      const isMobileLayout = viewportWidth > 0 && viewportWidth <= 768
      const items = Array.isArray(task && task.bundleItems) ? task.bundleItems : []
      const width = Math.max(280, Math.min(420, Math.max(280, viewportWidth - 24)))
      const maxHeight = Math.max(180, Math.min(320, 96 + items.length * 68))
      const spaceBelow = Math.max(0, viewportHeight - anchorRect.bottom - 16)
      const spaceAbove = Math.max(0, anchorRect.top - 16)
      const placement = spaceBelow >= maxHeight || spaceBelow >= spaceAbove ? 'below' : 'above'
      const left = isMobileLayout
        ? this.clampBundlePopoverPosition(
            (viewportWidth - width) / 2,
            8,
            Math.max(8, viewportWidth - width - 8)
          )
        : this.clampBundlePopoverPosition(
            anchorRect.left + anchorRect.width / 2 - width / 2,
            8,
            Math.max(8, viewportWidth - width - 8)
          )
      const estimatedHeight = maxHeight
      const top = placement == 'below'
        ? this.clampBundlePopoverPosition(anchorRect.bottom + 12, 8, Math.max(8, viewportHeight - estimatedHeight - 8))
        : this.clampBundlePopoverPosition(anchorRect.top - estimatedHeight - 12, 8, Math.max(8, viewportHeight - estimatedHeight - 8))
      const arrowLeft = isMobileLayout
        ? this.clampBundlePopoverPosition(width / 2 - 8, 18, Math.max(18, width - 18))
        : this.clampBundlePopoverPosition(anchorRect.left + anchorRect.width / 2 - left - 8, 18, Math.max(18, width - 18))
      return {
        left,
        top,
        width,
        maxHeight,
        arrowLeft,
        placement,
        anchorLeft: anchorRect.left,
        anchorTop: anchorRect.top,
        anchorWidth: anchorRect.width,
        anchorHeight: anchorRect.height
      }
    },
    updateBundlePopoverPosition() {
      const task = this.bundlePopoverTask
      if (!task || !this.$refs.bundlePopover || typeof this.$refs.bundlePopover.getBoundingClientRect != 'function') {
        return
      }
      const rect = this.$refs.bundlePopover.getBoundingClientRect()
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : rect.width
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : rect.height
      const isMobileLayout = viewportWidth > 0 && viewportWidth <= 768
      const left = isMobileLayout
        ? this.clampBundlePopoverPosition((viewportWidth - rect.width) / 2, 8, Math.max(8, viewportWidth - rect.width - 8))
        : this.clampBundlePopoverPosition(rect.left, 8, Math.max(8, viewportWidth - rect.width - 8))
      const top = this.clampBundlePopoverPosition(rect.top, 8, Math.max(8, viewportHeight - rect.height - 8))
      const anchorLeft = Number(this.bundlePopover.anchorLeft) || 0
      const anchorWidth = Number(this.bundlePopover.anchorWidth) || 0
      const arrowLeft = isMobileLayout
        ? this.clampBundlePopoverPosition(rect.width / 2 - 8, 18, Math.max(18, rect.width - 18))
        : this.clampBundlePopoverPosition(anchorLeft + anchorWidth / 2 - left - 8, 18, Math.max(18, rect.width - 18))
      this.bundlePopover = Object.assign({}, this.bundlePopover, { left, top, arrowLeft })
    },
    openBundlePopover(task, event) {
      const anchorRect = this.getBundlePopoverAnchorRect(event)
      if (!task || !anchorRect || !this.hasBundleItemList(task)) {
        this.closeBundlePopover()
        return
      }
      this.closeBundleFailureDialog()
      const next = this.calculateBundlePopoverPosition(task, anchorRect)
      this.bundlePopover = Object.assign({}, next, {
        visible: true,
        taskId: task.id
      })
      this.$nextTick(this.updateBundlePopoverPosition)
    },
    toggleBundlePopover(task, event) {
      if (!task || !this.hasBundleItemList(task)) {
        return
      }
      if (this.bundlePopover.visible && this.bundlePopover.taskId == task.id) {
        this.closeBundlePopover()
        return
      }
      this.openBundlePopover(task, event)
    },
    closeBundlePopover() {
      this.closeBundleFailureDialog()
      if (!this.bundlePopover.visible) {
        return
      }
      this.bundlePopover = Object.assign({}, this.bundlePopover, {
        visible: false,
        taskId: ''
      })
    },
    openBundleItemFailureDialog(task, item) {
      if (!task || !item || item.state != 'error') {
        return
      }
      this.bundleFailureDialog = {
        visible: true,
        taskId: task.id,
        itemId: item.id
      }
    },
    closeBundleFailureDialog() {
      if (!this.bundleFailureDialog.visible) {
        return
      }
      this.bundleFailureDialog = {
        visible: false,
        taskId: '',
        itemId: ''
      }
    },
    confirmBundleItemRetry() {
      const task = this.bundleFailureDialogTask
      const item = this.bundleFailureDialogItem
      if (!task || !item || item.state == 'running') {
        this.closeBundleFailureDialog()
        return
      }
      this.closeBundleFailureDialog()
      this.$emit('retry-bundle-item', task, item)
    },
    startDrag(event) {
      this.closeBundlePopover()
      if (event.type == 'mousedown' && event.button !== 0) {
        return
      }
      const target = event.target
      if (target && target.closest && target.closest('button, a, input, textarea, select')) {
        return
      }
      const point = this.getDragPoint(event)
      if (!point || !this.$el || typeof this.$el.getBoundingClientRect != 'function') {
        return
      }
      const rect = this.$el.getBoundingClientRect()
      const position = this.clampPanelPosition(rect.left, rect.top, rect.width, rect.height)
      this.dragPosition = position
      this.dragState = {
        offsetX: point.clientX - position.left,
        offsetY: point.clientY - position.top,
        width: rect.width,
        height: rect.height
      }
      this.addDragListeners()
      this.disableTextSelection()
      if (event.cancelable !== false) {
        event.preventDefault()
      }
    },
    handleDragMove(event) {
      if (!this.dragState) {
        return
      }
      const point = this.getDragPoint(event)
      if (!point) {
        return
      }
      const nextLeft = point.clientX - this.dragState.offsetX
      const nextTop = point.clientY - this.dragState.offsetY
      this.dragPosition = this.clampPanelPosition(
        nextLeft,
        nextTop,
        this.dragState.width,
        this.dragState.height
      )
      if (event.cancelable !== false) {
        event.preventDefault()
      }
    },
    stopDrag() {
      if (!this.dragState) {
        return
      }
      this.dragState = null
      this.removeDragListeners()
      this.restoreTextSelection()
      this.$nextTick(this.keepPanelInViewport)
    },
    addDragListeners() {
      if (typeof window === 'undefined') {
        return
      }
      this.removeDragListeners()
      window.addEventListener('mousemove', this.handleDragMove)
      window.addEventListener('mouseup', this.stopDrag)
      window.addEventListener('touchmove', this.handleDragMove, { passive: false })
      window.addEventListener('touchend', this.stopDrag)
      window.addEventListener('touchcancel', this.stopDrag)
    },
    removeDragListeners() {
      if (typeof window === 'undefined') {
        return
      }
      window.removeEventListener('mousemove', this.handleDragMove)
      window.removeEventListener('mouseup', this.stopDrag)
      window.removeEventListener('touchmove', this.handleDragMove)
      window.removeEventListener('touchend', this.stopDrag)
      window.removeEventListener('touchcancel', this.stopDrag)
    },
    getDragPoint(event) {
      if (event.touches && event.touches.length > 0) {
        return event.touches[0]
      }
      if (event.changedTouches && event.changedTouches.length > 0) {
        return event.changedTouches[0]
      }
      if (typeof event.clientX == 'number' && typeof event.clientY == 'number') {
        return event
      }
      return null
    },
    clampPanelPosition(left, top, width, height) {
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : width
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : height
      const margin = 0
      const maxLeft = Math.max(margin, viewportWidth - width - margin)
      const maxTop = Math.max(margin, viewportHeight - height - margin)
      return {
        left: Math.min(Math.max(left, margin), maxLeft),
        top: Math.min(Math.max(top, margin), maxTop)
      }
    },
    keepPanelInViewport() {
      if (!this.dragPosition || !this.$el || typeof this.$el.getBoundingClientRect != 'function') {
        if (this.bundlePopover.visible) {
          this.$nextTick(this.updateBundlePopoverPosition)
        }
        return
      }
      const rect = this.$el.getBoundingClientRect()
      this.dragPosition = this.clampPanelPosition(rect.left, rect.top, rect.width, rect.height)
      if (this.bundlePopover.visible) {
        this.$nextTick(this.updateBundlePopoverPosition)
      }
    },
    keepPanelBottomAfterSizeChange(value, oldValue) {
      if (value === oldValue) {
        this.$nextTick(this.keepPanelInViewport)
        return
      }
      if (!this.dragPosition || !this.$el || typeof this.$el.getBoundingClientRect != 'function') {
        this.$nextTick(this.keepPanelInViewport)
        return
      }
      const previousRect = this.$el.getBoundingClientRect()
      const previousBottom = previousRect.bottom
      this.$nextTick(() => {
        if (!this.dragPosition || !this.$el || typeof this.$el.getBoundingClientRect != 'function') {
          return
        }
        const rect = this.$el.getBoundingClientRect()
        this.dragPosition = this.clampPanelPosition(
          rect.left,
          previousBottom - rect.height,
          rect.width,
          rect.height
        )
      })
    },
    disableTextSelection() {
      if (typeof document === 'undefined' || !document.body) {
        return
      }
      this.previousBodyUserSelect = document.body.style.userSelect
      document.body.style.userSelect = 'none'
    },
    restoreTextSelection() {
      if (typeof document === 'undefined' || !document.body) {
        return
      }
      document.body.style.userSelect = this.previousBodyUserSelect
    }
  }
}
</script>

<style scoped>
.paper-download-list {
  position: fixed;
  margin: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: none;
  max-height: 30vh;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #d8d8d8;
  box-shadow: 0 8px 22px rgba(0, 0, 0, .12);
  z-index: 4500;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.paper-download-list.collapsed {
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-height: 56px;
}

.paper-download-list.dragging {
  transition: none;
}

.paper-download-list.dragging > header {
  cursor: grabbing;
}

.paper-download-list > header {
  height: 56px;
  padding: 0 18px;
  border-bottom: 1px solid #e5e5e2;
  display: flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  flex-shrink: 0;
  cursor: move;
  user-select: none;
  touch-action: none;
}

.paper-download-list.collapsed > header {
  border-bottom: 0;
}

.paper-download-list > header strong {
  color: #111;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  white-space: nowrap;
}

.paper-download-list > header span {
  min-width: 0;
  color: #666;
  font-size: 14px;
  line-height: 20px;
  flex: 1;
}

.paper-download-list > header button {
  border: 0;
  background: transparent;
  color: #1f3a2d;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.paper-download-task-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px 16px 14px;
}

.paper-download-task {
  padding: 12px 4px 14px;
  border-bottom: 1px solid #eeeeeb;
}

.paper-download-task:last-child {
  border-bottom: 0;
}

.paper-download-task-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.paper-download-task-head strong {
  min-width: 0;
  color: #111;
  font-size: 14px;
  line-height: 20px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1;
}

.paper-download-task-head span,
.paper-download-task-status {
  color: #666;
  font-size: 14px;
  line-height: 20px;
}

.paper-download-task-foot {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 4px;
}

.paper-download-task-status {
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 2px;
}

.paper-download-task-status-text {
  min-width: 0;
}

.paper-download-task-progress-button {
  border: 0;
  background: transparent;
  color: #1f3a2d;
  font-size: 14px;
  line-height: 20px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
  text-decoration: none;
}

.paper-download-task-progress-bracket {
  text-decoration: none;
}

.paper-download-task-progress-number {
  text-decoration: none;
}

.paper-download-task-foot button {
  border: 0;
  background: transparent;
  color: #1f3a2d;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.paper-download-progress {
  height: 6px;
  margin-top: 10px;
  background: #e4e7e2;
  overflow: hidden;
}

.paper-download-progress i {
  display: block;
  height: 100%;
  background: #1f3a2d;
  transition: width .18s ease;
}

.paper-download-bundle-popover {
  position: fixed;
  z-index: 4600;
  background: #fff;
  border: 1px solid #d8d8d8;
  box-shadow: 0 12px 28px rgba(0, 0, 0, .14);
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.paper-download-bundle-popover > header {
  height: 50px;
  min-height: 50px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #ecece8;
  box-sizing: border-box;
}

.paper-download-bundle-popover-title {
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
}

.paper-download-bundle-popover-title strong {
  min-width: 0;
  flex: 0 1 auto;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 14px;
  line-height: 20px;
  color: #111;
}

.paper-download-bundle-popover-title span {
  flex: none;
  color: #666;
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;
}

.paper-download-bundle-popover > header button {
  flex: none;
  border: 0;
  background: transparent;
  color: #1f3a2d;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.paper-download-bundle-popover-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px 14px 12px;
}

.paper-download-bundle-popover-item {
  padding: 10px 4px 12px;
  border-bottom: 1px solid #eeeeeb;
}

.paper-download-bundle-popover-item:last-child {
  border-bottom: 0;
}

.paper-download-bundle-popover-item-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.paper-download-bundle-popover-item-head strong {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 13px;
  line-height: 18px;
  color: #111;
}

.paper-download-bundle-popover-item-head span,
.paper-download-bundle-popover-item-status {
  color: #666;
  font-size: 13px;
  line-height: 18px;
}

.paper-download-bundle-popover-item-foot {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.paper-download-bundle-popover-item-status {
  min-width: 0;
  flex: 1;
}

.paper-download-bundle-popover-item-foot button {
  border: 0;
  background: transparent;
  color: #1f3a2d;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.paper-download-bundle-popover-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
}

.paper-download-bundle-popover.is-below .paper-download-bundle-popover-arrow {
  top: -10px;
  border-bottom: 10px solid #d8d8d8;
}

.paper-download-bundle-popover.is-below .paper-download-bundle-popover-arrow::after {
  content: '';
  position: absolute;
  left: -9px;
  top: 1px;
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-bottom: 9px solid #fff;
}

.paper-download-bundle-popover.is-above .paper-download-bundle-popover-arrow {
  bottom: -10px;
  border-top: 10px solid #d8d8d8;
}

.paper-download-bundle-popover.is-above .paper-download-bundle-popover-arrow::after {
  content: '';
  position: absolute;
  left: -9px;
  top: -10px;
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-top: 9px solid #fff;
}

.paper-download-bundle-failure-mask {
  position: fixed;
  inset: 0;
  z-index: 4700;
  background: rgba(0, 0, 0, .32);
  display: flex;
  align-items: center;
  justify-content: center;
}

.paper-download-bundle-failure-modal {
  width: 420px;
  max-width: calc(100vw - 24px);
  background: #fff;
  border: 1px solid #d8d8d8;
  box-shadow: 0 12px 28px rgba(0, 0, 0, .16);
  box-sizing: border-box;
  padding: 22px 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.paper-download-bundle-failure-modal > header {
  color: #1d1d1f;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  margin: 0;
  word-break: break-word;
}

.paper-download-bundle-failure-reason {
  min-height: 56px;
  max-height: 42vh;
  overflow: auto;
  color: #333;
  font-size: 14px;
  line-height: 22px;
  white-space: pre-wrap;
  word-break: break-word;
}

.paper-download-bundle-failure-modal footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.paper-download-bundle-failure-modal footer > div {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.paper-download-bundle-failure-modal footer > div button {
  min-width: 68px;
  height: 32px;
  border: 1px solid #d8d8d8;
  background: #fff;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  padding: 0 12px;
}

.paper-download-bundle-failure-modal footer > div button:last-child {
  border-color: #1f3a2d;
  background: #1f3a2d;
  color: #fff;
}
</style>
